class CountrySelector {
  constructor() {
    this.section = document.querySelector('[data-country-selector-section]');
    this.insertPosition = this.section.dataset.insertPosition;
    this.mobileInsertPosition = this.section.dataset.mobileInsertPosition;
    this.customSelector = this.section.dataset.customSelector;
    this.mobileCustomSelector = this.section.dataset.mobileCustomSelector;
    this.enableIpDetection = this.section.dataset.enableIpDetection === 'true';
    this.defaultCountry = this.section.dataset.defaultCountry;
    this.countryOptions = this.loadCountryOptions();
    // 定义欧洲国家代码列表
    this.europeanCountries = [
      'al', 'ad', 'at', 'by', 'be', 'ba', 'bg', 'hr', 'cz', 'dk', 'ee', 'fi', 'fr', 'de', 'gr', 'hu',
      'is', 'ie', 'it', 'lv', 'li', 'lt', 'lu', 'mk', 'mt', 'md', 'mc', 'me', 'nl', 'no', 'pl', 'pt',
      'ro', 'ru', 'sm', 'rs', 'sk', 'si', 'es', 'se', 'ch', 'ua', 'gb', 'va'
    ];
    this.init();
  }

  loadCountryOptions() {
    const configEl = this.section.querySelector('[data-country-selector-config]');
    let countries = [];

    if (configEl) {
      try {
        countries = JSON.parse(configEl.textContent).countries || [];
      } catch (e) {
        // ignore invalid config
      }
    }

    if (!countries.length) {
      countries = Array.from(this.section.querySelectorAll('.country-selector__option'))
        .map(option => ({
          code: option.getAttribute('data-country-code'),
          href: option.href,
          name: option.querySelector('.country-selector__option-name')?.textContent?.trim(),
        }));
    }

    const baseDomain = this.getBaseDomainFromCountries(countries);
    return countries
      .filter(country => country.code && country.href)
      .map(country => {
        const countryCode = country.code.toLowerCase();
        const href = country.href;
        const optionHost = this.normalizeHost(new URL(href).hostname);
        let subdomain = null;

        if (baseDomain && optionHost !== baseDomain && optionHost.endsWith('.' + baseDomain)) {
          subdomain = optionHost.slice(0, -(baseDomain.length + 1));
        }

        return { countryCode, href, optionHost, subdomain, name: country.name };
      });
  }

  getBaseDomainFromCountries(countries) {
    const usCountry = countries.find(country => country.code?.toLowerCase() === 'us' && country.href);
    if (usCountry?.href) {
      return this.normalizeHost(new URL(usCountry.href).hostname);
    }

    const firstCountry = countries.find(country => country.href);
    if (firstCountry?.href) {
      const host = this.normalizeHost(new URL(firstCountry.href).hostname);
      const parts = host.split('.');
      if (parts.length >= 2) {
        return parts.slice(-2).join('.');
      }
    }

    return null;
  }

  async init() {
    await this.updateDisplayedCountry();
    this.moveSectionToPosition();
    this.setupEventListeners();

    window.addEventListener('popstate', () => this.updateDisplayedCountry());
  }

  normalizeHost(hostname) {
    return hostname.toLowerCase().replace(/^www\./, '');
  }

  findOptionByHost(url = window.location.href) {
    const currentHost = this.normalizeHost(new URL(url).hostname);

    for (const option of this.getCountryOptions()) {
      if (option.optionHost === currentHost) {
        return { option, currentHost, optionHost: option.optionHost };
      }
    }

    return null;
  }

  async updateDisplayedCountry() {
    try {
      const currentUrl = window.location.href;
      const hasVisited = !!localStorage.getItem('hasVisited');

      if (hasVisited) {
        const hostMatch = this.findOptionByHost(currentUrl);

        if (hostMatch) {
          const { option: matchedOption } = hostMatch;
          const countryCode = matchedOption.countryCode;
          const domOption = this.findDomOptionByCode(countryCode);
          if (!domOption) return;

          const flagCode = domOption.querySelector('.country-selector__flag-wrapper').innerHTML;
          const countryName = domOption.querySelector('.country-selector__option-name').textContent;
          const language = domOption.querySelector('.country-selector__option-language').textContent;

          document.querySelectorAll('[data-country-selector-section]').forEach(section => {
            const currentFlag = section.querySelector('.country-selector__current .country-selector__flag-wrapper');
            const currentName = section.querySelector('.country-selector__current .country-selector__name');
            const currentLanguage = section.querySelector('.country-selector__current .country-selector__language');

            if (currentFlag) currentFlag.innerHTML = flagCode;
            if (currentName) currentName.textContent = countryName;
            if (currentLanguage) currentLanguage.textContent = language;
          });
          return;
        }
      }

      const countryCode = await this.setDefaultCountry();
      if (!countryCode) return;

      const countryOption = this.findDomOptionByCode(countryCode);

      if (countryOption) {
        const flagCode = countryOption.querySelector('.country-selector__flag-wrapper').innerHTML;
        const countryName = countryOption.querySelector('.country-selector__option-name').textContent;
        const language = countryOption.querySelector('.country-selector__option-language').textContent;

        document.querySelectorAll('[data-country-selector-section]').forEach(section => {
          const currentFlag = section.querySelector('.country-selector__current .country-selector__flag-wrapper');
          const currentName = section.querySelector('.country-selector__current .country-selector__name');
          const currentLanguage = section.querySelector('.country-selector__current .country-selector__language');

          if (currentFlag) currentFlag.innerHTML = flagCode;
          if (currentName) currentName.textContent = countryName;
          if (currentLanguage) currentLanguage.textContent = language;
        });
      }
    } catch (error) {
      // ignore display update errors
    }
  }

  getCountryCodeFromUrl() {
    const url = new URL(window.location.href);

    const countryParam = url.searchParams.get('country');
    if (countryParam) {
      return countryParam.toLowerCase();
    }

    const hostMatch = this.findOptionByHost(url.href);
    if (hostMatch) {
      return hostMatch.option.countryCode;
    }

    return null;
  }

  getCountryOptions() {
    return this.countryOptions;
  }

  findDomOptionByCode(countryCode) {
    if (!countryCode) return null;
    return document.querySelector(`.country-selector__option[data-country-code="${countryCode.toLowerCase()}"]`);
  }

  findCountryOptionByCode(countryCode) {
    if (!countryCode) return null;
    return this.getCountryOptions().find(o => o.countryCode === countryCode.toLowerCase()) || null;
  }

  resolveCountryCode(rawCode) {
    const options = this.getCountryOptions();

    if (!rawCode) {
      return this.defaultCountry;
    }

    const code = rawCode.toLowerCase();

    const directMatch = options.find(o => o.countryCode === code);
    if (directMatch) {
      return directMatch.countryCode;
    }

    const subdomainMatch = options.find(o => o.subdomain === code);
    if (subdomainMatch) {
      return subdomainMatch.countryCode;
    }

    // IP/ISO 用 gb，站点配置常用 uk，需双向别名
    const aliasMatch = options.find(o => {
      if (code === 'gb' && (o.countryCode === 'uk' || o.subdomain === 'uk')) return true;
      if (code === 'uk' && (o.countryCode === 'gb' || o.subdomain === 'gb')) return true;
      return false;
    });
    if (aliasMatch) {
      return aliasMatch.countryCode;
    }

    const dedicatedKeys = new Set(
      options.flatMap(o => [o.countryCode, o.subdomain].filter(Boolean))
    );
    // 已配置英国站时，gb/uk 都视为独立站点，避免落入欧洲兜底
    if (dedicatedKeys.has('uk') || dedicatedKeys.has('gb')) {
      dedicatedKeys.add('uk');
      dedicatedKeys.add('gb');
    }

    if (this.europeanCountries.includes(code) && !dedicatedKeys.has(code) && options.some(o => o.countryCode === 'eu')) {
      return 'eu';
    }

    return this.defaultCountry;
  }

  isEuropeanFallback(ipCountry, resolvedCode) {
    if (!ipCountry || resolvedCode !== 'eu') return false;
    const code = ipCountry.toLowerCase();
    if (code === 'eu') return false;
    return this.europeanCountries.includes(code) && !this.findCountryOptionByCode(code);
  }

  redirectToCountry(countryCode) {
    const countryOption = this.findCountryOptionByCode(countryCode);
    if (!countryOption?.href) return false;

    localStorage.setItem('hasVisited', 'true');
    window.location.replace(countryOption.href);
    return true;
  }

  async setDefaultCountry() {
    const countryCodeFromUrl = this.getCountryCodeFromUrl();
    const hasVisited = !!localStorage.getItem('hasVisited');

    if (hasVisited) {
      if (countryCodeFromUrl) {
        return countryCodeFromUrl;
      }
      return this.defaultCountry;
    }

    if (this.enableIpDetection) {
      try {
        const ipCountry = await this.getCountryByIp();

        if (countryCodeFromUrl) {
          if (!ipCountry) {
            localStorage.setItem('hasVisited', 'true');
            return countryCodeFromUrl;
          }

          const ipResolved = this.resolveCountryCode(ipCountry);

          if (this.isEuropeanFallback(ipCountry, ipResolved) && countryCodeFromUrl !== 'eu') {
            localStorage.setItem('hasVisited', 'true');
            return countryCodeFromUrl;
          }

          if (ipResolved === countryCodeFromUrl) {
            localStorage.setItem('hasVisited', 'true');
            return countryCodeFromUrl;
          }

          if (this.redirectToCountry(ipResolved)) {
            return null;
          }

          localStorage.setItem('hasVisited', 'true');
          return countryCodeFromUrl;
        }

        if (!ipCountry) {
          if (this.redirectToCountry(this.defaultCountry)) {
            return null;
          }
          localStorage.setItem('hasVisited', 'true');
          return this.defaultCountry;
        }

        const countryCodeToUse = this.resolveCountryCode(ipCountry);
        const needRedirect = countryCodeToUse !== this.getCountryCodeFromUrl();

        if (needRedirect && this.redirectToCountry(countryCodeToUse)) {
          return null;
        }

        localStorage.setItem('hasVisited', 'true');
        return countryCodeToUse;
      } catch (error) {
        if (countryCodeFromUrl) {
          localStorage.setItem('hasVisited', 'true');
          return countryCodeFromUrl;
        }

        if (this.redirectToCountry(this.defaultCountry)) {
          return null;
        }
        localStorage.setItem('hasVisited', 'true');
        return this.defaultCountry;
      }
    }

    if (countryCodeFromUrl) {
      localStorage.setItem('hasVisited', 'true');
      return countryCodeFromUrl;
    }

    if (this.redirectToCountry(this.defaultCountry)) {
      return null;
    }
    localStorage.setItem('hasVisited', 'true');
    return this.defaultCountry;
  }

  async getCountryByIp() {
    if (this._ipCountryCode) return this._ipCountryCode;

    const providers = [
      {
        url: 'https://ipapi.co/json/',
        parse: (data) => data.country_code?.toLowerCase() || null,
      },
      {
        url: 'https://ipwho.is/',
        parse: (data) => (data.success === false ? null : data.country_code?.toLowerCase() || null),
      },
    ];

    for (const provider of providers) {
      try {
        const response = await fetch(provider.url);
        if (!response.ok) continue;

        const data = await response.json();
        const countryCode = provider.parse(data);
        if (countryCode) {
          this._ipCountryCode = countryCode;
          return countryCode;
        }
      } catch (error) {
        // try next provider
      }
    }

    return null;
  }

  isMobileDevice() {
    return window.innerWidth <= 768;
  }

  initializeSelector(selectorElement) {
    const trigger = selectorElement.querySelector('[data-country-selector-trigger]');
    const modal = selectorElement.querySelector('[data-country-selector-modal]');
    const closeButton = selectorElement.querySelector('[data-country-selector-close]');
    const options = selectorElement.querySelectorAll('.country-selector__option');

    trigger.addEventListener('click', () => {
      modal.style.display = 'block';
      setTimeout(() => modal.classList.add('country-selector__modal--visible'), 10);
      options[0].focus();
    });

    const closeModal = () => {
      modal.classList.remove('country-selector__modal--visible');
      setTimeout(() => modal.style.display = 'none', 300);
    };

    closeButton.addEventListener('click', closeModal);
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        closeModal();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.style.display === 'block') {
        closeModal();
      }
    });

    options.forEach((option, index) => {
      option.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight' && index < options.length - 1) {
          options[index + 1].focus();
        } else if (e.key === 'ArrowLeft' && index > 0) {
          options[index - 1].focus();
        } else if (e.key === 'ArrowDown' && index + 3 < options.length) {
          options[index + 3].focus();
        } else if (e.key === 'ArrowUp' && index - 3 >= 0) {
          options[index - 3].focus();
        }
      });

      option.addEventListener('click', () => {
        localStorage.setItem('hasVisited', 'true');
      });
    });
  }

  moveSectionToPosition() {
    const isMobile = this.isMobileDevice();

    document.querySelectorAll('[data-country-selector-section]').forEach(el => el.remove());

    const pcSelector = this.section.cloneNode(true);
    const mobileSelector = this.section.cloneNode(true);
    mobileSelector.classList.add('mobile-country-selector');

    this.initializeSelector(pcSelector);
    this.initializeSelector(mobileSelector);

    let pcInserted = false;
    if (this.insertPosition === 'custom' && this.customSelector) {
      const targetElement = document.querySelector(this.customSelector);
      if (targetElement) {
        targetElement.appendChild(pcSelector);
        pcInserted = true;
      }
    } else if (this.insertPosition === 'header') {
      const header = document.querySelector('.header-item--icons');
      if (header) {
        header.insertBefore(pcSelector, header.firstChild);
        pcInserted = true;
      }
    } else if (this.insertPosition === 'footer') {
      const footer = document.querySelector('.footer-info .grid');
      if (footer) {
        footer.appendChild(pcSelector);
        pcInserted = true;
      }
    }

    let mobileInserted = false;
    if (this.mobileInsertPosition === 'custom' && this.mobileCustomSelector) {
      const targetElement = document.querySelector(this.mobileCustomSelector);
      if (targetElement) {
        targetElement.appendChild(mobileSelector);
        mobileInserted = true;
      }
    } else if (this.mobileInsertPosition === 'header') {
      const header = document.querySelector('.slide-nav.e_slide-nav');
      if (header) {
        header.appendChild(mobileSelector);
        mobileInserted = true;
      }
    } else if (this.mobileInsertPosition === 'footer') {
      const footer = document.querySelector('.footer-info .grid');
      if (footer) {
        footer.appendChild(mobileSelector);
        mobileInserted = true;
      }
    }

    document.querySelectorAll('[data-country-selector-section]').forEach(el => {
      if (el.classList.contains('mobile-country-selector')) {
        if (mobileInserted) {
          el.style.display = isMobile ? 'block' : 'none';
          el.classList.add('country-selector--visible');
        } else {
          el.style.display = 'none';
          el.classList.remove('country-selector--visible');
        }
      } else {
        if (pcInserted) {
          el.style.display = isMobile ? 'none' : 'block';
          el.classList.add('country-selector--visible');
        } else {
          el.style.display = 'none';
          el.classList.remove('country-selector--visible');
        }
      }
    });
  }

  setupEventListeners() {
    window.addEventListener('resize', () => this.moveSectionToPosition());
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new CountrySelector();
});
